import { DEFAULT_CLUB_TIMEZONE } from '@/core/models/club'

/**
 * Single source of truth for every locale-dependent VALUE in the app —
 * UI language, region number/date formatting, currency, country, timezone.
 *
 * This is the seam for localization. Today the app is Spain-only (the product
 * is Spain-first — see `docs/product/business-model.md`), so `DEFAULT_LOCALE`
 * is hardcoded to {@link SPAIN_LOCALE}. Going multi-locale later means deriving
 * the active config per club/user instead of hardcoding it here — call sites
 * that already read `DEFAULT_LOCALE` (e.g. `shared/utils/format.ts`) don't
 * change. Pair with the i18next string catalog in `core/i18n/i18n.ts`.
 */
export interface LocaleConfig {
  /** BCP-47 tag for `Intl` date/number/currency formatters, e.g. `'es-ES'`. */
  readonly intlLocale: string
  /** i18next UI language code, e.g. `'es'`. */
  readonly language: string
  /** ISO 4217 currency for `Intl` currency formatting, e.g. `'EUR'`. */
  readonly currency: string
  /** ISO 3166-1 alpha-2 country, e.g. `'ES'`. */
  readonly countryCode: string
  /** IANA timezone for the club's local clock, e.g. `'Europe/Madrid'`. */
  readonly timezone: string
}

export const SPAIN_LOCALE: LocaleConfig = {
  intlLocale: 'es-ES',
  language: 'es',
  currency: 'EUR',
  countryCode: 'ES',
  timezone: DEFAULT_CLUB_TIMEZONE,
}

/**
 * The app's ACTIVE locale config. Spain for now. When a second locale/country
 * becomes real, replace this constant with a club/user-derived lookup — every
 * formatter already reads from here, so the change is localized to this file.
 */
export const DEFAULT_LOCALE: LocaleConfig = SPAIN_LOCALE
